import { useRef } from 'react';
import { Box, Container, Grid, Typography, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import PageHero from '../components/common/PageHero';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CTABanner from '../components/common/CTABanner';
import { useServices } from '../hooks/usePublicApi';

const navy  = '#0F1D2E';
const navyL = '#1B2D42';
const gold  = '#D1BC8A';
const goldD = '#B8A06E';

const svcGradients = [
  `linear-gradient(140deg, ${navy} 0%, #2A4562 100%)`,
  `linear-gradient(140deg, #1A4828 0%, #2D7042 100%)`,
  `linear-gradient(140deg, #2D1B4F 0%, #503080 100%)`,
];

// ── Icon resolver: handles React components (static fallback) and strings (API) ─
function resolveIcon(icon) {
  if (typeof icon === 'function' || (typeof icon === 'object' && icon?.$$typeof)) return icon;
  if (typeof icon === 'string') {
    try { return require('../admin/utils/iconMap').getIcon(icon); } catch { return null; }
  }
  return null;
}

// ── Blueprint overlay ─────────────────────────────────────────────────────────
function Blueprint({ opacity = 0.06 }) {
  return (
    <Box sx={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)
      `,
      backgroundSize: '36px 36px',
    }} />
  );
}

// ── Scroll-triggered fade-up ──────────────────────────────────────────────────
function InView({ children, delay = 0, style, variants }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-70px 0px' });
  const defaultVariants = {
    hidden:  { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] } },
  };
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={variants || defaultVariants}
      style={{ height: '100%', ...style }}
    >
      {children}
    </motion.div>
  );
}

// ── Gradient service overview card ────────────────────────────────────────────
function ServiceOverviewCard({ service, gradient, index }) {
  const Icon = resolveIcon(service.icon || service.icon_name);
  return (
    <motion.div
      whileHover={{ translateY: -8, boxShadow: '0 28px 60px rgba(0,0,0,0.22)' }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      style={{ height: '100%' }}
    >
      <Box
        component={RouterLink}
        to={service.path || `/${service.slug}`}
        sx={{
          display: 'flex', flexDirection: 'column',
          textDecoration: 'none',
          borderRadius: '22px', overflow: 'hidden',
          background: gradient,
          position: 'relative', minHeight: 300,
          boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          '&:hover .svc-arrow': { transform: 'translateX(5px)' },
          '&:hover .svc-glow': { opacity: 1 },
        }}
      >
        <Blueprint opacity={0.06} />
        <Box className="svc-glow" sx={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.055)', opacity: 0, transition: 'opacity 0.35s ease' }} />

        <Box sx={{ position: 'relative', p: { xs: 3, md: 3.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Number */}
          <Typography sx={{
            position: 'absolute', top: 16, right: 20,
            color: 'rgba(255,255,255,0.06)', fontWeight: 900,
            fontSize: '5rem', lineHeight: 1, userSelect: 'none',
          }}>
            {String(index + 1).padStart(2, '0')}
          </Typography>

          {/* Icon box */}
          <Box sx={{
            width: 52, height: 52, borderRadius: '16px', mb: 3,
            bgcolor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.14)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {Icon && <Icon sx={{ fontSize: 26, color: 'rgba(255,255,255,0.9)' }} />}
          </Box>

          <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.5rem', lineHeight: 1.2, mb: 1.5 }}>
            {service.title}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.88rem', lineHeight: 1.72, flex: 1 }}>
            {service.description}
          </Typography>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.83rem' }}>Mehr erfahren</Typography>
            <Box className="svc-arrow" sx={{ transition: 'transform 0.3s ease' }}>
              <ArrowForwardIcon sx={{ color: gold, fontSize: 17, display: 'block' }} />
            </Box>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Feature item ──────────────────────────────────────────────────────────────
function FeatureItem({ feature, delay = 0 }) {
  const FeatureIcon = resolveIcon(feature.icon || feature.icon_name);
  return (
    <InView delay={delay} style={{ height: 'auto' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{
          width: 40, height: 40, borderRadius: '12px', flexShrink: 0, mt: 0.25,
          background: 'linear-gradient(135deg, rgba(209,188,138,0.14) 0%, rgba(209,188,138,0.07) 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: gold,
        }}>
          {FeatureIcon && <FeatureIcon sx={{ fontSize: 20 }} />}
        </Box>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.4, color: navy }}>
            {feature.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            {feature.description}
          </Typography>
        </Box>
      </Stack>
    </InView>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Leistungen() {
  const { services } = useServices();
  const serviceList = services || [];
  return (
    <>
      <Helmet>
        <title>Leistungen — Neubau, Umbau & Sanierung | Süd Ausbau GmbH</title>
        <meta name="description" content="Unser Leistungsspektrum: Neubau, Umbau und Sanierung in München und Umgebung. Alle Leistungen der Süd Ausbau GmbH auf einen Blick." />
      </Helmet>

      <PageHero overline="Unsere Leistungen" title="Alles aus einer Hand" />

      {/* ── Service overview cards ───────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <InView style={{ height: 'auto' }}>
            <Box sx={{ mb: { xs: 5, md: 7 } }}>
              <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>
                Leistungsbereiche
              </Typography>
              <Typography variant="h2" sx={{ color: navy, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
                Was wir für Sie tun
              </Typography>
            </Box>
          </InView>

          <Grid container spacing={2.5}>
            {serviceList.map((service, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={service.slug || service.path || i}>
                <InView delay={i * 0.1} style={{ height: 'auto' }}>
                  <ServiceOverviewCard service={service} gradient={svcGradients[i % svcGradients.length]} index={i} />
                </InView>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Per-service detail sections ──────────────────────────────────────── */}
      {serviceList.map((service, idx) => {
        const Icon = resolveIcon(service.icon || service.icon_name);
        const isEven = idx % 2 === 0;
        return (
          <Box
            key={service.id || service.slug}
            sx={{ py: { xs: 8, md: 12 }, bgcolor: isEven ? '#F8F7F5' : '#fff', position: 'relative', overflow: 'hidden' }}
          >
            {/* Faint section number watermark */}
            <Typography sx={{
              position: 'absolute',
              top: { xs: -20, md: -40 },
              right: { xs: -10, md: 0 },
              color: 'rgba(15,29,46,0.03)',
              fontWeight: 900,
              fontSize: { xs: '10rem', md: '16rem' },
              lineHeight: 1,
              userSelect: 'none',
              pointerEvents: 'none',
            }}>
              {String(idx + 1).padStart(2, '0')}
            </Typography>

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
              <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
                {/* Left: header + description + CTA */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <InView style={{ height: 'auto' }}>
                    {/* Service icon + label */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
                        background: `linear-gradient(135deg, rgba(209,188,138,0.16) 0%, rgba(209,188,138,0.08) 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: gold,
                      }}>
                        {Icon && <Icon sx={{ fontSize: 26 }} />}
                      </Box>
                      <Typography sx={{
                        color: gold, fontWeight: 700, fontSize: '0.72rem',
                        textTransform: 'uppercase', letterSpacing: '0.14em',
                      }}>
                        {service.subtitle}
                      </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{
                      color: navy, fontWeight: 900, lineHeight: 1.1,
                      letterSpacing: '-0.025em', mb: 2.5,
                    }}>
                      {service.title}
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 4 }}>
                      {service.description}
                    </Typography>

                    {/* Process steps compact */}
                    <Stack spacing={1.5} sx={{ mb: 4 }}>
                      {service.process.map((step, i) => (
                        <Stack key={i} direction="row" spacing={2} alignItems="flex-start">
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <CheckIcon sx={{ fontSize: 14, color: navy, fontWeight: 900 }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: navy, mb: 0.1 }}>
                              {step.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.83rem' }}>
                              {step.description}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>

                    <Button
                      variant="contained"
                      size="large"
                      component={RouterLink}
                      to={service.path || `/${service.slug}`}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
                        color: navy, fontWeight: 800,
                        px: 3, py: 1.4, borderRadius: '12px',
                        boxShadow: `0 8px 24px rgba(209,188,138,0.28)`,
                        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 16px 40px rgba(209,188,138,0.38)` },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Mehr zu {service.title}
                    </Button>
                  </InView>
                </Grid>

                {/* Right: feature grid */}
                <Grid size={{ xs: 12, md: 7 }}>
                  <Grid container spacing={2.5}>
                    {service.features.map((feature, i) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={i}>
                        <FeatureItem feature={feature} delay={i * 0.07} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </Box>
        );
      })}

      <CTABanner pageKey="leistungen" />
    </>
  );
}
